import React, { ComponentProps, useCallback } from 'react';
import { Controller } from 'react-hook-form';

import { Box } from '@mui/material';
import { DatePickerProps } from '@mui/x-date-pickers';

import {
  DatePicker,
  TextFieldProps,
  useWindowedTooltipZIndex,
  withTitle,
  withWindowedPopperProps,
} from '@sbv-clients/ui-shared';
import { Density } from '@sbv-clients/util-misc';

import { useFormFieldError } from '../../hooks/use-field-error';

type PickerPropsType = {
  minDate?: DatePickerProps['minDate'];
  maxDate?: DatePickerProps['maxDate'];
  onOpen?: DatePickerProps['onOpen'];
  shouldDisableDate?: DatePickerProps['shouldDisableDate'];
};

type TextFieldPropsType = {
  name: string;
  label?: TextFieldProps['label'];
  fullWidth?: TextFieldProps['fullWidth'];
  disabled?: TextFieldProps['disabled'];
  required?: TextFieldProps['required'];
  size?: TextFieldProps['size'];
  onChange?: (value: Date | null, name: string) => void;
  onBlur?: () => void;
  sx?: object;
  placeholder?: TextFieldProps['placeholder'];
  clearable?: boolean;
  density?: Density;
};

type FormDatePickerFieldProps = PickerPropsType & TextFieldPropsType;
type RenderType = ComponentProps<typeof Controller>['render'];

export const FormDatePickerFieldNoTitle = ({
  name,
  fullWidth,
  disabled,
  required,
  size,
  minDate,
  maxDate,
  onOpen,
  onChange,
  sx,
  shouldDisableDate,
  placeholder,
  clearable,
  density = 'compact',
}: FormDatePickerFieldProps) => {
  const { error, helperText } = useFormFieldError(name);
  const windowedConfig = useWindowedTooltipZIndex();

  const render: RenderType = useCallback(
    ({ field }) => {
      return (
        <Box>
          <DatePicker
            {...field}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            onOpen={onOpen}
            shouldDisableDate={shouldDisableDate}
            onChange={(value) => {
              field.onChange(value);
              onChange?.(value, name);
            }}
            slotProps={{
              textField: {
                disabled,
                fullWidth,
                required,
                size,
                placeholder,
                variant: 'outlined',
                sx: {
                  ...sx,
                  '& .MuiPickersInputBase-root': {
                    height: density === 'comfortable' ? '42px' : '32px',
                  },
                },
                ...(error && {
                  error: true,
                  helperText: helperText,
                }),
                onKeyDown: (event: React.KeyboardEvent) => {
                  const invalidKeys = /^[a-zA-Z]$/;
                  if (invalidKeys.test(event.key)) {
                    event.preventDefault();
                  }
                },
              },
              field: { clearable },
              popper: withWindowedPopperProps(windowedConfig, undefined),
            }}
            sx={sx}
          />
        </Box>
      );
    },
    [
      disabled,
      minDate,
      maxDate,
      onOpen,
      shouldDisableDate,
      fullWidth,
      required,
      size,
      placeholder,
      sx,
      density,
      error,
      helperText,
      clearable,
      windowedConfig,
      onChange,
      name,
    ]
  );

  return <Controller name={name} render={render} />;
};

export const FormDatePickerField = withTitle(FormDatePickerFieldNoTitle);
